import { useMemo, useState } from 'react';

import { Button } from 'primereact/button';

import { TOA_GetFertilizationsForFreezing } from '@/rtk-query.gql';

import { SelectionService } from '../utils/selectionService';
import { EmbryoSelection } from '../utils/types';

type FreezeRatiosProps = {
  className?: string;
  fertilizations: TOA_GetFertilizationsForFreezing[] | undefined;
};

export const FreezeMetrics = ({
  fertilizations,
  className,
}: FreezeRatiosProps) => {
  const [selected, setSelected] = useState<EmbryoSelection>();

  const totals = useMemo(() => {
    return (
      fertilizations?.reduce(
        (acc, fert) => {
          acc.totalMatings = fertilizations.length;
          if ((fert.embs || 0) > 0) {
            acc.matingsWithLive++;
          }
          const qualityEmbryos = fert.predictedGood || 0;
          const lowQualityEmbryos = fert.predictedVeryLow || 0;
          const totalEmbryos = qualityEmbryos + lowQualityEmbryos;

          acc.totalEmbryos += totalEmbryos;
          acc.frozenEmbryos += fert.embs || 0;
          acc.qualityEmbryosToFreeze += qualityEmbryos;

          if (fert.semenType?.startsWith('RS')) {
            acc.rsIvc += fert.ivcNumber || 0;
            acc.rsCleaved += fert.cleaved || 0;
            acc.rsQualityEmbryos += fert.predictedGood || 0;
          } else if (fert.semenType?.includes('SF')) {
            acc.sfIvc += fert.ivcNumber || 0;
            acc.sfCleaved += fert.cleaved || 0;
            acc.sfQualityEmbryos += fert.predictedGood || 0;
          } else {
            acc.convIvc += fert.ivcNumber || 0;
            acc.convCleaved += fert.cleaved || 0;
            acc.convQualityEmbryos += fert.predictedGood || 0;
          }

          return acc;
        },
        {
          totalMatings: 0,
          matingsWithLive: 0,
          totalEmbryos: 0,
          frozenEmbryos: 0,
          qualityEmbryosToFreeze: 0,
          convIvc: 0,
          convCleaved: 0,
          convQualityEmbryos: 0,
          sfIvc: 0,
          sfCleaved: 0,
          sfQualityEmbryos: 0,
          rsIvc: 0,
          rsCleaved: 0,
          rsQualityEmbryos: 0,
        },
      ) || {
        totalMatings: 0,
        matingsWithLive: 0,
        totalEmbryos: 0,
        frozenEmbryos: 0,
        qualityEmbryosToFreeze: 0,
        convIvc: 0,
        convCleaved: 0,
        convQualityEmbryos: 0,
        sfIvc: 0,
        sfCleaved: 0,
        sfQualityEmbryos: 0,
        rsIvc: 0,
        rsCleaved: 0,
        rsQualityEmbryos: 0,
      }
    );
  }, [fertilizations]);

  const rates = {
    convCleavageRate: totals?.convIvc
      ? (totals.convCleaved / totals.convIvc) * 100
      : 0,
    convQedrRate: totals?.convIvc
      ? (totals.convQualityEmbryos / totals.convIvc) * 100
      : 0,
    sfCleavageRate: totals?.sfIvc ? (totals.sfCleaved / totals.sfIvc) * 100 : 0,
    sfQedrRate: totals?.sfIvc
      ? (totals.sfQualityEmbryos / totals.sfIvc) * 100
      : 0,
    rsCleavageRate: totals?.rsIvc ? (totals.rsCleaved / totals.rsIvc) * 100 : 0,
    rsQedrRate: totals?.rsIvc
      ? (totals.rsQualityEmbryos / totals.rsIvc) * 100
      : 0,
  };

  const handleSelect = () => {
    const selected = SelectionService.getSelection();
    setSelected(selected);
  };

  return (
    <div className={`freeze-ratios ${className ? ` ${className}` : ''}`}>
      <div className="freeze__fertilization-totals-container">
        <>
          <div className="freeze__fertilization-totals-wrapper">
            <div className="freeze__fertilization-totals">
              <span>
                Matings:
                <strong>{totals?.totalMatings}</strong>
              </span>
              <span>
                Matings w/ Live Embs:
                <strong>{totals?.matingsWithLive}</strong>
              </span>
            </div>
          </div>
          <div className="freeze__fertilization-totals-wrapper">
            <div className="freeze__fertilization-totals">
              <span>
                Embs:
                <strong>{totals?.totalEmbryos}</strong>
              </span>
              <span>
                Embs Frozen Via App:
                <strong>{totals?.frozenEmbryos}</strong>
              </span>
            </div>
          </div>
        </>

        <div className="freeze__fertilization-totals-wrapper">
          <div className="freeze__fertilization-totals">
            <span>
              Total Quality Embs Holding:
              <strong>{totals?.qualityEmbryosToFreeze}</strong>
            </span>
          </div>
        </div>

        <div className="freeze__fertilization-totals-wrapper get-selected-rows">
          <Button onClick={handleSelect}>Get Selected Rows</Button>
          <div className="freeze__fertilization-totals">
            <span>
              <strong>
                {((selected?.liveEmbryoIds?.length as number) || 0) +
                  ((selected?.tempEmbryoIds?.length as number) || 0)}{' '}
                Embryos from{' '}
                {((selected?.fertilizationIds?.length as number) || 0) === 1
                  ? ((selected?.fertilizationIds?.length as number) || 0) +
                    ' mating'
                  : ((selected?.fertilizationIds?.length as number) || 0) +
                    ' matings'}
              </strong>
            </span>
          </div>
        </div>
      </div>
      <div className="freeze__fertilization-totals-container">
        <div className="freeze__fertilization-totals-wrapper">
          <div className="freeze__fertilization-rate">
            <span>
              Clv% (CONV):
              <strong>{rates?.convCleavageRate.toFixed(1)}%</strong>
            </span>
          </div>
          <div className="freeze__fertilization-rate">
            <span>
              QEDR% (CONV):
              <strong>{rates?.convQedrRate.toFixed(1)}%</strong>
            </span>
          </div>
        </div>

        <div className="freeze__fertilization-totals-wrapper">
          <div className="freeze__fertilization-rate">
            <span className="smaller-rate">
              Clv% (SF):
              <strong>{rates?.sfCleavageRate.toFixed(1)}%</strong>
            </span>
          </div>
          <div className="freeze__fertilization-rate">
            <span className="smaller-rate">
              QEDR% (SF):
              <strong>{rates?.sfQedrRate.toFixed(1)}%</strong>
            </span>
          </div>
        </div>
        <div className="freeze__fertilization-totals-wrapper">
          <div className="freeze__fertilization-rate">
            <span className="smaller-rate">
              Clv% (RS):
              <strong>{rates?.rsCleavageRate.toFixed(1)}%</strong>
            </span>
          </div>
          <div className="freeze__fertilization-rate">
            <span className="smaller-rate">
              QEDR% (RS):
              <strong>{rates?.rsQedrRate.toFixed(1)}%</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
